import React, {useCallback, useEffect, useState} from 'react';
import { useAuth } from "../../services/AuthProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Input, Typography } from "@mui/material";
import {activateAccount, resendConfirmationCode} from "../../services/HttpService";
import { useError } from "../../services/ErrorContext";
import styles from "../Login/Login.module.css";
import EnvelopeIcon from "../../resources/Login/envelope.png";
import {ConfirmationCodePattern, UsernamePattern} from "../../services/Util";
import SnackbarAlertMessage from "../../components/Modal/SnackbarAlertMessage";

const Activate: React.FC = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { setError } = useError();
  const [username, setUsername] = useState('');
  const [inputUsername, setInputUsername] = useState<string>('');
  const [openResendCodeSuccessMessage, setOpenResendCodeSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const activate = useCallback(async (usernameToActivate: string, codeToActivate: string) => {
    try {
      setIsLoading(true);
      await activateAccount(usernameToActivate, codeToActivate);
      setIsLoading(false);
      navigate(`/login?username=${encodeURIComponent(usernameToActivate)}`);
    } catch (error: any) {
      if (error.message.indexOf("400") > 0 && error.message.indexOf("User is already confirmed") > 0) {
        setIsLoading(false);
        navigate(`/login?username=${encodeURIComponent(usernameToActivate)}`);
      } else {
        setError(error.message);
      }
    }
  }, [navigate, setError]);

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/dashboard');
    }

    const searchParams = new URLSearchParams(location.search);
    const usernameParam = searchParams.get('username');
    const codeParam = searchParams.get('code');
    if (usernameParam && UsernamePattern.test(decodeURIComponent(usernameParam))) {
      setUsername(decodeURIComponent(usernameParam));
    }

    // Automatically submit if both username and code are present
    if (usernameParam && codeParam && UsernamePattern.test(decodeURIComponent(usernameParam)) && ConfirmationCodePattern.test(codeParam)) {
      activate(usernameParam, codeParam);
    }

  }, [isLoggedIn, navigate, location, activate]);

  const resendCode = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const usernameToActivate = UsernamePattern.test(username) ? username : UsernamePattern.test(inputUsername) ? inputUsername : "";
    if (!usernameToActivate) {
      setError("No valid username detected, please refresh browser and try again.");
      return;
    }

    try {
      await resendConfirmationCode(usernameToActivate);
      setOpenResendCodeSuccessMessage(true);
    } catch (error: any) {
      if (error.message.indexOf("400") > 0 && error.message.indexOf("User is already confirmed") > 0) {
        setOpenResendCodeSuccessMessage(true);
        setError("Your account is already confirmed, now login please");
        navigate(`/login?username=${encodeURIComponent(usernameToActivate)}`);
      } else {
        setError(error.message);
      }
    }
  };

  return (
    <div className={styles.Login} data-testid="activate">
      <SnackbarAlertMessage
        open={openResendCodeSuccessMessage}
        severity={"success"}
        message={"Activation Link Resent, check your email and click the button"}
        onClose={() => setOpenResendCodeSuccessMessage(false)}
      />

      <section id="ActivateTitle" className={styles.LoginTitle}>
        <Typography variant="h1">Activate your account</Typography>
      </section>

      <br /><br /><br />
      {
        isLoading ?
          <div style={{paddingBottom: 80}}>Activating for you ...</div> :
          <Box sx={{ width: '80%', maxWidth: 500, textAlign: 'center' }}>
            <img src={EnvelopeIcon} alt="key" />
            <br /><br />
            <Typography variant="h3" gutterBottom>Check your email</Typography>
            <br />
            <Typography variant="caption" gutterBottom>An activation email has been sent to your email address.</Typography>
            <br />
            <Typography variant="caption" gutterBottom>Please open and read the email to continue.</Typography>
            <br /><br /><br />

            {/* Username Field */}
            {(!username) && (
              <Box sx={{ marginBottom: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ textAlign: 'left' }}>Username</Typography>
                <Input name="username" fullWidth disableUnderline placeholder="Enter your username" value={inputUsername} onChange={(e) => setInputUsername(e.target.value)}/>
              </Box>
            )}

            <br /><br /><br />

            <div style={{ marginBottom: 50 }}>
              <Typography variant="caption" gutterBottom>Didn't receive the email? Try again!</Typography>
              &nbsp;
              <Button onClick={resendCode}>Resend activate link</Button>
            </div>
          </Box>
      }
    </div>
  );
};

export default Activate;
